// THE STANDARD — Notification Bell
// Shows unread count badge, opens a popover with the last 30 in-app notifications.
// Always portaled to <body> so no app pane can paint over it.
// Phones: a full-screen sheet. Tablet and desktop: a dropdown under the bell.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bell, X, CheckCheck, Dumbbell, Flame, MessageSquare, Trophy, Star, Info,
  Calendar, ListChecks, BookOpen, Activity, Utensils, UserPlus, Megaphone,
  DollarSign, CalendarDays, Trash2,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useLocation } from 'wouter';
import { useAuth } from '@/_core/hooks/useAuth';

const TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  // Reminders (scheduled self-directed nudges)
  checkin_reminder: Flame,
  session_reminder: Calendar,
  habit_reminder: ListChecks,
  weekly_report: Star,
  weekly_recap_share: Star,
  monday_new_week: CalendarDays,
  faith_directive: BookOpen,
  // Activity (client did something / coach-relevant)
  coach_activity: Activity,
  carb_cycle_drift: Utensils,
  pr: Trophy,
  'pr.achieved': Trophy,
  shoutout: Megaphone,
  coach_feedback: MessageSquare,
  follow: UserPlus,
  follow_approved: UserPlus,
  wall_post: MessageSquare,
  wall_comment: MessageSquare,
  wall_mention: MessageSquare,
  wall_reaction: MessageSquare,
  application_deposit: DollarSign,
  workout: Dumbbell,
  system: Info,
};

// Filter buckets. "Reminders" = scheduled nudges sent TO the viewer about
// their own tasks; everything else is "Activity" (client check-ins/workouts
// for coaches via the coach_activity mirror, social events for clients).
type FeedFilter = 'all' | 'reminder' | 'activity';
const REMINDER_TYPES = new Set([
  'checkin_reminder', 'session_reminder', 'habit_reminder',
  'weekly_report', 'weekly_recap_share', 'monday_new_week', 'faith_directive',
]);
function bucketOf(type: string): Exclude<FeedFilter, 'all'> {
  return REMINDER_TYPES.has(type) ? 'reminder' : 'activity';
}

// Fallback deep-link target derived from type + title for historical
// notifications inserted before senders started populating actionUrl.
// New notifications carry their own actionUrl from the server; this keeps
// older rows in the bell from being dead-ends.
function deriveActionUrl(n: { type: string; title: string; actionUrl: string | null }): string | null {
  if (n.actionUrl) return n.actionUrl;
  const t = n.type;
  const title = (n.title ?? '').toLowerCase();
  if (t === 'habit_reminder' || title.includes('daily habits')) return '/client/habits';
  if (t === 'session_reminder' || title.includes('session reminder') || title.includes('we missed you')) return '/client/schedule';
  if (t === 'checkin_reminder') {
    return title.includes('weekly') ? '/client/check-in' : '/client';
  }
  if (t === 'weekly_report') return '/client/weekly-reports';
  if (t === 'workout') return '/client/training';
  return null;
}

function NotificationPanel({
  notifications,
  unreadCount,
  filter,
  setFilter,
  filterLabels,
  onClose,
  onClickNotification,
  onMarkAllRead,
  onDeleteNotification,
  onDeleteAll,
}: {
  notifications: any[];
  unreadCount: number;
  filter: FeedFilter;
  setFilter: (f: FeedFilter) => void;
  filterLabels: Record<FeedFilter, string>;
  onClose: () => void;
  onClickNotification: (n: any) => void;
  onMarkAllRead: () => void;
  onDeleteNotification: (id: number) => void;
  onDeleteAll: () => void;
}) {
  const visible = filter === 'all' ? notifications : notifications.filter((n) => bucketOf(n.type) === filter);
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-1 border-b border-border flex-shrink-0">
        <p className="text-sm font-bold text-foreground">Notifications</p>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="flex min-h-11 items-center gap-1 text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors uppercase tracking-wider"
            >
              <CheckCheck className="w-3 h-3" />
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={onDeleteAll}
              className="flex min-h-11 items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-red-400 transition-colors uppercase tracking-wider"
            >
              <Trash2 className="w-3 h-3" />
              Delete all
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close notifications" className="-mr-2 flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-border/50 flex-shrink-0">
        {(['all', 'reminder', 'activity'] as FeedFilter[]).map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              aria-pressed={active}
              className="px-2.5 py-1 pointer-coarse:min-h-11 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors"
              style={{
                background: active ? 'var(--primary)' : 'var(--surface-2, rgba(255,255,255,0.05))',
                color: active ? 'var(--primary-foreground, #0a0a0a)' : 'var(--text-secondary, #a1a1aa)',
                border: '1px solid var(--hairline, rgba(255,255,255,0.08))',
              }}
            >
              {filterLabels[f]}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <Bell className="w-8 h-8 text-muted-foreground/40 mb-2" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">
              {filter === 'all' ? 'No notifications yet' : `No ${filterLabels[filter].toLowerCase()} yet`}
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">Check-in reminders and coach updates will appear here</p>
          </div>
        ) : (
          visible.map((n) => {
            const Icon = TYPE_ICON[n.type] ?? Info;
            const isUnread = !n.readAt;
            return (
              // div+role, not <button>: the per-row delete control below is a
              // real <button> and buttons cannot nest.
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                onClick={() => onClickNotification(n)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClickNotification(n); } }}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left cursor-pointer hover:bg-[#e8eef1] active:opacity-60 transition-all border-b border-border/50 last:border-0 ${isUnread ? 'bg-primary/5' : ''}`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${isUnread ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}>
                    {n.title}
                  </p>
                  {n.body && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{n.body}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground/60 mt-1">
                    {new Date(n.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
                {isUnread && (
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteNotification(n.id); }}
                  className="flex-shrink-0 -my-2 -mr-3 flex size-11 items-center justify-center rounded text-muted-foreground/50 hover:text-red-400 transition-colors"
                  aria-label="Delete notification"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

const panelPalette = {
  background: '#f4f1eb',
  color: '#17151a',
  ['--primary' as never]: '#2f5d73',
  ['--primary-foreground' as never]: '#ffffff',
  ['--foreground' as never]: '#17151a',
  ['--muted-foreground' as never]: '#5d666d',
  ['--surface-2' as never]: '#e8eef1',
  ['--border' as never]: '#d7dcdf',
  ['--secondary' as never]: '#e8eef1',
  ['--text-secondary' as never]: '#5d666d',
};

/** Where the dropdown sits on tablet and desktop: just under the bell, right
 * edges aligned, kept inside the screen and its safe areas. */
export function notificationDropdownAnchor(
  bell: { bottom: number; right: number },
  viewportWidth: number,
): { top: number; right: number } {
  const right = Math.max(12, Math.round(viewportWidth - bell.right));
  return { top: Math.max(0, Math.round(bell.bottom + 8)), right };
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState({ top: 64, right: 12 });
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  // Pin the dropdown under the bell and follow it through resizes, rotation
  // and keyboard changes while open.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const bell = bellRef.current?.getBoundingClientRect();
      if (!bell) return;
      setAnchor(notificationDropdownAnchor(bell, window.innerWidth));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('orientationchange', place);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('orientationchange', place);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [open]);

  // Escape closes and hands focus back to the bell.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      bellRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  const [filter, setFilter] = useState<FeedFilter>('all');
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const { user } = useAuth();

  // Coaches see their own reminders vs client activity; everyone else gets
  // neutral labels (their reminders vs social/other activity).
  const isCoach = (user as { role?: string } | null)?.role === 'coach' || (user as { role?: string } | null)?.role === 'admin';
  const filterLabels: Record<FeedFilter, string> = isCoach
    ? { all: 'All', reminder: 'My Reminders', activity: 'Client Activity' }
    : { all: 'All', reminder: 'Reminders', activity: 'Activity' };

  const { data: unreadCount = 0 } = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: 60_000, // poll every 60s
  });
  const { data: notifications = [] } = trpc.notifications.list.useQuery(undefined, {
    enabled: open,
  });

  const markOne = trpc.notifications.markOneRead.useMutation({
    onSuccess: () => {
      utils.notifications.unreadCount.invalidate();
      utils.notifications.list.invalidate();
    },
  });
  const markAll = trpc.notifications.markAllRead.useMutation({
    onSuccess: () => {
      utils.notifications.unreadCount.invalidate();
      utils.notifications.list.invalidate();
    },
  });
  const deleteOne = trpc.notifications.delete.useMutation({
    onSuccess: () => {
      utils.notifications.unreadCount.invalidate();
      utils.notifications.list.invalidate();
    },
  });
  const deleteAll = trpc.notifications.deleteAll.useMutation({
    onSuccess: () => {
      utils.notifications.unreadCount.invalidate();
      utils.notifications.list.invalidate();
    },
  });

  const handleClick = (n: (typeof notifications)[0]) => {
    if (!n.readAt) markOne.mutate({ id: n.id });
    const target = deriveActionUrl(n);
    if (target) {
      navigate(target);
      setOpen(false);
    }
  };

  const panelProps = {
    notifications,
    unreadCount: unreadCount as number,
    filter,
    setFilter,
    filterLabels,
    onClose: close,
    onClickNotification: handleClick,
    onMarkAllRead: () => markAll.mutate(),
    onDeleteNotification: (id: number) => deleteOne.mutate({ id }),
    onDeleteAll: () => {
      if (confirm('Delete all notifications? This cannot be undone.')) deleteAll.mutate();
    },
  };

  return (
    <div className="relative">
      <button
        ref={bellRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 active:opacity-60 transition-all"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-primary text-primary-foreground text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* One panel for every size, portaled to <body>. Rendering it inside the
          top bar trapped it in the bar's stacking context (the glass bar's
          backdrop-filter creates one), so on iPad the message thread pane
          painted over it. Phones get a full-screen sheet; tablets and desktop
          get a dropdown pinned under the bell. */}
      {open && createPortal(
        <div data-contrast="light" data-notification-layer="">
          <div
            aria-hidden="true"
            data-testid="notification-backdrop"
            className="fixed inset-0 z-[10000]"
            onClick={close}
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            tabIndex={-1}
            data-testid="notification-panel"
            className="notification-panel premium-light-surface fixed inset-x-0 bottom-0 top-[env(safe-area-inset-top,0px)] z-[10001] flex flex-col overflow-hidden outline-none md:inset-auto md:top-[var(--nb-top)] md:right-[max(var(--nb-right),calc(12px+env(safe-area-inset-right,0px)))] md:w-[min(24rem,calc(100vw-1.5rem))] md:max-h-[var(--nb-max-height)] md:rounded-2xl md:border md:border-border md:shadow-2xl"
            style={{
              ...panelPalette,
              ['--nb-top' as never]: `${anchor.top}px`,
              ['--nb-right' as never]: `${anchor.right}px`,
              ['--nb-max-height' as never]: `calc(var(--app-shell-height, 100dvh) - max(var(--kb, 0px), var(--web-kb, 0px)) - ${anchor.top}px - env(safe-area-inset-bottom, 0px) - 12px)`,
            }}
          >
            {/* Small gap under the status bar on phones */}
            <div className="flex-shrink-0 h-1.5 md:hidden" />
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <NotificationPanel {...panelProps} />
            </div>
            {/* Safe area bottom spacer on phones */}
            <div className="flex-shrink-0 md:hidden" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
